// Lone Sport — Quick view (Gymshark-style reference layout)
// Uses Shopify public product JSON: /products/<handle>.js

(() => {
  const MODAL_ID = 'lone-quickview';

  /** Optional promo strip under wishlist/share — edit copy here. */
  const PROMO_HEADLINE = 'SHOP FOR UP TO 50% OFF* NOW';
  const PROMO_SUB =
    'Mid-season sale on select styles. Discounts applied in cart where applicable — see product page for details.';

  const SIZE_GUIDE_URL = '/pages/size-chart';

  const money = (cents, currency = '$') => {
    if (typeof cents !== 'number') return '';
    return `${currency}${(cents / 100).toFixed(2)}`;
  };

  const findOptionIndex = (product, names) => {
    const lower = new Set(names.map((n) => n.toLowerCase()));
    const idx = (product.options || []).findIndex((o) => lower.has(String(o.name || '').toLowerCase()));
    return idx >= 0 ? idx : null;
  };

  /** Quick view shows S / M / L / XL in this order when the product has matching variant option values. */
  const SIZE_SLOT_ORDER = ['S', 'M', 'L', 'XL'];

  const sizeDisplaySlot = (raw) => {
    const u = String(raw || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, ' ');
    if (!u) return null;
    if (u === 'S' || u === 'SMALL') return 'S';
    if (u === 'M' || u === 'MEDIUM' || u === 'MED') return 'M';
    if (u === 'L' || u === 'LARGE') return 'L';
    if (u === 'XL' || u === 'XLARGE' || u === 'X-LARGE' || u === 'X LARGE' || u === 'EXTRA LARGE' || u === 'EXTRA-LARGE')
      return 'XL';
    return null;
  };

  /** @returns {{ display: string, raw: string }[]} */
  const buildSmxlSlots = (variants, sizeIdx) => {
    if (sizeIdx == null) return [];
    const raws = Array.from(new Set(variants.map((v) => (v.options || [])[sizeIdx]).filter(Boolean)));
    const slotToRaw = new Map();
    for (const raw of raws) {
      const slot = sizeDisplaySlot(raw);
      if (slot && SIZE_SLOT_ORDER.includes(slot) && !slotToRaw.has(slot)) slotToRaw.set(slot, raw);
    }
    return SIZE_SLOT_ORDER.filter((d) => slotToRaw.has(d)).map((display) => ({ display, raw: slotToRaw.get(display) }));
  };

  const minAvailablePrice = (vars) => {
    const ok = (vars || []).filter((v) => v && v.available && typeof v.price === 'number');
    if (!ok.length) return null;
    return Math.min(...ok.map((v) => v.price));
  };

  const getModal = () => document.getElementById(MODAL_ID);

  const ensureStyles = () => {
    let style = document.getElementById('lone-quickview-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'lone-quickview-styles';
      document.head.appendChild(style);
    }
    style.textContent = `
      .lone-qv { position: fixed; inset: 0; z-index: 90; display: none; font-family: inherit; }
      .lone-qv.is-open { display: block; }
      .lone-qv__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.65); }
      .lone-qv__dialog {
        position: absolute; inset: 12px; background: #0a0a0a; border-radius: 0;
        overflow: hidden; box-shadow: 0 24px 80px rgba(0,0,0,.7);
        display: flex; flex-direction: column; border: 1px solid rgba(255,255,255,.1);
      }
      @media (min-width: 720px) { .lone-qv__dialog { inset: 20px; border-radius: 2px; } }

      .lone-qv__close {
        position: absolute; top: 10px; right: 10px; z-index: 5;
        width: 40px; height: 40px; border: 1px solid rgba(255,255,255,.15); border-radius: 2px;
        background: #1a1a1a; cursor: pointer; display: flex; align-items: center; justify-content: center;
      }
      .lone-qv__close svg { width: 18px; height: 18px; stroke: #ffffff; fill: none; stroke-width: 2; }

      .lone-qv__layout {
        flex: 1; min-height: 0; display: grid; grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
      }
      @media (min-width: 960px) {
        .lone-qv__layout {
          grid-template-columns: minmax(0, 1.15fr) minmax(300px, 420px);
          grid-template-rows: 1fr;
        }
      }

      /* Gallery */
      .lone-qv__gallery {
        display: flex; gap: 12px; padding: 16px 12px 16px 16px; background: #0a0a0a;
        align-items: flex-start; min-height: 0;
      }
      @media (min-width: 960px) { .lone-qv__gallery { padding: 24px 16px 24px 24px; } }

      .lone-qv__rail-col { display: flex; flex-direction: column; align-items: center; gap: 6px; flex-shrink: 0; width: 56px; }
      .lone-qv__rail-nav {
        width: 32px; height: 28px; border: 1px solid rgba(255,255,255,.15); border-radius: 2px; background: #1a1a1a;
        cursor: pointer; font-size: 12px; line-height: 1; color: #ffffff; padding: 0; flex-shrink: 0;
      }
      .lone-qv__rail-nav:disabled { opacity: .35; cursor: default; }
      .lone-qv__rail-scroll {
        max-height: min(68vh, 560px); overflow-y: auto; overflow-x: hidden;
        display: flex; flex-direction: column; gap: 8px; width: 100%;
        scrollbar-width: thin; scrollbar-color: rgba(255,255,255,.2) transparent;
      }
      .lone-qv__rail-scroll::-webkit-scrollbar { width: 4px; }
      .lone-qv__rail-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.2); border-radius: 2px; }

      .lone-qv__thumb {
        border: 0; padding: 0; margin: 0; width: 100%; aspect-ratio: 3/4; cursor: pointer;
        border-radius: 2px; overflow: hidden; background: #111111;
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); flex-shrink: 0;
      }
      .lone-qv__thumb.is-active { box-shadow: inset 0 0 0 2px #ffffff; }
      .lone-qv__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

      .lone-qv__hero { flex: 1; min-width: 0; position: relative; align-self: stretch; display: flex; align-items: center; justify-content: center; background: #0a0a0a; }
      .lone-qv__hero-inner { position: relative; width: 100%; max-height: min(72vh, 640px); overflow: hidden; display: flex; align-items: center; justify-content: center; }
      .lone-qv__hero-inner.is-zoomed { cursor: zoom-out; }
      .lone-qv__hero-inner:not(.is-zoomed) { cursor: default; }
      .lone-qv__stage-img {
        display: block; max-width: 100%; max-height: min(72vh, 640px); width: auto; height: auto;
        object-fit: contain; transition: transform .2s ease;
      }
      .lone-qv__hero-inner.is-zoomed .lone-qv__stage-img { transform: scale(1.65); cursor: zoom-out; }
      .lone-qv__zoom {
        position: absolute; bottom: 12px; right: 12px; width: 36px; height: 36px; border-radius: 999px;
        border: 1px solid rgba(255,255,255,.15); background: #1a1a1a; font-size: 20px; font-weight: 600; line-height: 1;
        cursor: pointer; display: flex; align-items: center; justify-content: center; color: #ffffff; z-index: 2;
      }

      /* Right panel */
      .lone-qv__panel {
        padding: 20px 20px 24px; overflow-y: auto; border-top: 1px solid rgba(255,255,255,.1);
        display: flex; flex-direction: column; gap: 0; min-height: 0; background: #0a0a0a; color: #ffffff;
      }
      @media (min-width: 960px) {
        .lone-qv__panel { border-top: 0; border-left: 1px solid rgba(255,255,255,.1); padding: 28px 28px 32px; }
      }

      .lone-qv__discount-pill {
        display: inline-flex; align-self: flex-start; margin-bottom: 12px;
        padding: 6px 10px; font-size: 11px; font-weight: 700; letter-spacing: .04em;
        text-transform: uppercase; background: #e00000; color: #ffffff; border-radius: 999px;
      }
      .lone-qv__discount-pill[hidden] { display: none !important; }

      .lone-qv__title {
        margin: 0 0 6px; font-size: clamp(1.15rem, 2.2vw, 1.65rem); font-weight: 800;
        letter-spacing: .04em; text-transform: uppercase; line-height: 1.15; color: #ffffff;
      }
      .lone-qv__subtitle { margin: 0 0 14px; font-size: 13px; color: rgba(255,255,255,.5); font-weight: 500; }

      .lone-qv__prices { display: flex; flex-wrap: wrap; align-items: baseline; gap: 10px 14px; margin-bottom: 16px; }
      .lone-qv__price-now { font-size: 18px; font-weight: 800; color: #ffffff; }
      .lone-qv__price-was { font-size: 16px; font-weight: 600; color: rgba(255,255,255,.4); text-decoration: line-through; }
      .lone-qv__price-was[hidden] { display: none !important; }

      .lone-qv__engage { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 16px; margin-bottom: 16px; }
      .lone-qv__rating { font-size: 13px; color: rgba(255,255,255,.55); display: none; align-items: center; gap: 6px; }
      .lone-qv__rating.is-visible { display: flex; }

      .lone-qv__sq {
        width: 44px; height: 44px; border: 1px solid rgba(255,255,255,.2); border-radius: 50%; background: #1a1a1a;
        cursor: pointer; display: flex; align-items: center; justify-content: center; color: #ffffff; padding: 0; flex-shrink: 0;
      }
      .lone-qv__sq svg { width: 20px; height: 20px; display: block; fill: currentColor; stroke: none; }
      .lone-qv__sq[aria-pressed="true"] { box-shadow: inset 0 0 0 2px #ffffff; }

      .lone-qv__promo-block {
        background: #111111; padding: 14px 16px; margin-bottom: 20px; text-align: center; border-radius: 2px;
        border: 1px solid rgba(255,255,255,.08);
      }
      .lone-qv__promo-block[hidden] { display: none !important; }
      .lone-qv__promo-head { font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #ffffff; margin: 0 0 6px; }
      .lone-qv__promo-sub { font-size: 11px; line-height: 1.45; color: rgba(255,255,255,.5); margin: 0; }

      .lone-qv__swatches { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 8px; }
      .lone-qv__swatch {
        width: 48px; height: 48px; padding: 0; border: 2px solid transparent; border-radius: 2px; cursor: pointer;
        background: #1a1a1a; overflow: hidden; flex-shrink: 0; box-sizing: border-box;
      }
      .lone-qv__swatch.is-selected { border-color: #ffffff; }
      .lone-qv__swatch img { width: 100%; height: 100%; object-fit: cover; display: block; }

      .lone-qv__color-name { font-size: 12px; color: rgba(255,255,255,.6); margin-bottom: 18px; font-weight: 600; }

      .lone-qv__size-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
      .lone-qv__size-head span { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: rgba(255,255,255,.75); }
      .lone-qv__size-guide { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.75); text-decoration: underline; text-underline-offset: 2px; }

      .lone-qv__sizes { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
      .lone-qv__size {
        min-width: 48px; padding: 12px 10px; border: 1px solid rgba(255,255,255,.2); border-radius: 2px; background: #1a1a1a;
        font-size: 12px; font-weight: 700; cursor: pointer; color: #ffffff; flex: 0 0 auto; text-align: center;
      }
      .lone-qv__size.is-selected { border: 2px solid #ffffff; padding: 11px 9px; }
      .lone-qv__size.is-ghost { opacity: .45; cursor: not-allowed; }

      .lone-qv__atb {
        width: 100%; padding: 16px 18px; border-radius: 2px; border: 0; background: #ffffff; color: #0a0a0a;
        font-weight: 800; font-size: 12px; letter-spacing: .1em; text-transform: uppercase; cursor: pointer;
      }
      .lone-qv__atb:disabled { opacity: .4; cursor: not-allowed; }
      .lone-qv__hint { margin-top: 12px; font-size: 12px; color: rgba(255,255,255,.45); }
    `;
  };

  const ensureModal = () => {
    ensureStyles();
    let modal = getModal();
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'lone-qv';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="lone-qv__backdrop" data-qv-close></div>
      <div class="lone-qv__dialog" role="dialog" aria-modal="true" aria-label="Product quick view">
        <button type="button" class="lone-qv__close" data-qv-close aria-label="Close">
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <div class="lone-qv__layout">
          <div class="lone-qv__gallery">
            <div class="lone-qv__rail-col">
              <button type="button" class="lone-qv__rail-nav lone-qv__rail-nav--up" data-qv-rail-up aria-label="Scroll thumbnails up">▲</button>
              <div class="lone-qv__rail-scroll" data-qv-rail></div>
              <button type="button" class="lone-qv__rail-nav lone-qv__rail-nav--down" data-qv-rail-down aria-label="Scroll thumbnails down">▼</button>
            </div>
            <div class="lone-qv__hero">
              <div class="lone-qv__hero-inner" data-qv-hero-inner>
                <img class="lone-qv__stage-img" data-qv-stage alt="">
                <button type="button" class="lone-qv__zoom" data-qv-zoom aria-label="Zoom image">+</button>
              </div>
            </div>
          </div>
          <div class="lone-qv__panel">
            <div class="lone-qv__discount-pill" data-qv-discount hidden></div>
            <h2 class="lone-qv__title" data-qv-title></h2>
            <p class="lone-qv__subtitle" data-qv-subtitle hidden></p>
            <div class="lone-qv__prices">
              <span class="lone-qv__price-now" data-qv-price-now></span>
              <span class="lone-qv__price-was" data-qv-price-was hidden></span>
            </div>
            <div class="lone-qv__engage">
              <div class="lone-qv__rating" data-qv-rating aria-hidden="true"></div>
              <button type="button" class="lone-qv__sq" data-qv-wishlist aria-pressed="false" title="Wishlist">
                <svg class="lone-qv__heart" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
              </button>
              <button type="button" class="lone-qv__sq" data-qv-share title="Share">
                <svg class="lone-qv__share-ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path transform="scale(-1,1) translate(-24,0)" fill="currentColor" d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1C19.5 15.3 16.3 10 10 9z"/>
                </svg>
              </button>
            </div>
            <div class="lone-qv__promo-block" data-qv-promo>
              <p class="lone-qv__promo-head" data-qv-promo-head></p>
              <p class="lone-qv__promo-sub" data-qv-promo-sub></p>
            </div>
            <div data-qv-colors-block hidden>
              <div class="lone-qv__swatches" role="radiogroup" aria-label="Color" data-qv-colors></div>
              <div class="lone-qv__color-name" data-qv-color-label></div>
            </div>
            <div data-qv-sizes-block>
              <div class="lone-qv__size-head">
                <span>Select a size</span>
                <a class="lone-qv__size-guide" data-qv-size-guide href="${SIZE_GUIDE_URL}" target="_blank" rel="noopener">Size guide</a>
              </div>
              <div class="lone-qv__sizes" role="radiogroup" aria-label="Size" data-qv-sizes></div>
            </div>
            <button type="button" class="lone-qv__atb" data-qv-atb disabled>Add to bag</button>
            <div class="lone-qv__hint" data-qv-hint></div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-qv-close]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    return modal;
  };

  const open = () => {
    const modal = ensureModal();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    const modal = ensureModal();
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  const render = async ({ handle }) => {
    const modal = ensureModal();
    modal.dataset.handle = handle;

    const discountEl = modal.querySelector('[data-qv-discount]');
    const titleEl = modal.querySelector('[data-qv-title]');
    const subtitleEl = modal.querySelector('[data-qv-subtitle]');
    const priceNowEl = modal.querySelector('[data-qv-price-now]');
    const priceWasEl = modal.querySelector('[data-qv-price-was]');
    const railEl = modal.querySelector('[data-qv-rail]');
    const railUp = modal.querySelector('[data-qv-rail-up]');
    const railDown = modal.querySelector('[data-qv-rail-down]');
    const heroInner = modal.querySelector('[data-qv-hero-inner]');
    const zoomBtn = modal.querySelector('[data-qv-zoom]');
    const stageEl = modal.querySelector('[data-qv-stage]');
    const promoEl = modal.querySelector('[data-qv-promo]');
    const promoHeadEl = modal.querySelector('[data-qv-promo-head]');
    const promoSubEl = modal.querySelector('[data-qv-promo-sub]');
    const colorsBlock = modal.querySelector('[data-qv-colors-block]');
    const colorsEl = modal.querySelector('[data-qv-colors]');
    const colorLabelEl = modal.querySelector('[data-qv-color-label]');
    const sizesEl = modal.querySelector('[data-qv-sizes]');
    const atbBtn = modal.querySelector('[data-qv-atb]');
    const hintEl = modal.querySelector('[data-qv-hint]');
    const wishlistBtn = modal.querySelector('[data-qv-wishlist]');
    const shareBtn = modal.querySelector('[data-qv-share]');
    const sizeGuideA = modal.querySelector('[data-qv-size-guide]');

    railEl.innerHTML = '';
    colorsEl.innerHTML = '';
    sizesEl.innerHTML = '';
    heroInner.classList.remove('is-zoomed');
    atbBtn.disabled = true;
    atbBtn.textContent = 'Add to bag';
    hintEl.textContent = 'Loading…';

    promoHeadEl.textContent = PROMO_HEADLINE;
    promoSubEl.textContent = PROMO_SUB;
    promoEl.hidden = !(String(PROMO_HEADLINE || '').trim() || String(PROMO_SUB || '').trim());
    sizeGuideA.href = SIZE_GUIDE_URL;

    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to load product: ${handle}`);
    const product = await res.json();

    titleEl.textContent = String(product.title || '');
    const typeStr = String(product.type || product.product_type || '').trim();
    if (typeStr) {
      subtitleEl.textContent = typeStr;
      subtitleEl.hidden = false;
    } else {
      subtitleEl.textContent = '';
      subtitleEl.hidden = true;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) throw new Error('No variants');

    const images = Array.isArray(product.images) ? product.images : [];
    const featured = product.featured_image || images[0] || '';

    /** Resolved variant for cart + pricing once color & size are explicitly chosen. */
    let selected = null;

    const variantImageSrc = (v) => {
      if (v && v.featured_image && v.featured_image.src) return v.featured_image.src;
      return null;
    };

    const updateDiscountPill = (v) => {
      if (!v || !v.compare_at_price || v.compare_at_price <= v.price) {
        discountEl.hidden = true;
        discountEl.textContent = '';
        return;
      }
      const saveCents = v.compare_at_price - v.price;
      const pct = Math.round((saveCents / v.compare_at_price) * 100);
      discountEl.textContent = `${pct}% OFF | SAVE ${money(saveCents)}`;
      discountEl.hidden = false;
    };

    const setActiveThumbByUrl = (url) => {
      if (!url) return;
      let any = false;
      railEl.querySelectorAll('.lone-qv__thumb').forEach((b) => {
        const t = b.getAttribute('data-src') || '';
        const match = t && url && (url === t || url.includes(t) || t.includes(url));
        b.classList.toggle('is-active', !!match);
        if (match) any = true;
      });
      if (!any) railEl.querySelectorAll('.lone-qv__thumb').forEach((b) => b.classList.remove('is-active'));
    };

    const stageUrl = images[0] || featured || '';
    stageEl.src = stageUrl || '';
    stageEl.alt = product.title || '';

    const thumbList = images.length ? images.slice(0, 12) : featured ? [featured] : [];
    thumbList.forEach((url, idx) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `lone-qv__thumb ${idx === 0 ? 'is-active' : ''}`;
      btn.setAttribute('data-src', url);
      btn.innerHTML = `<img alt="" loading="lazy" src="${url}">`;
      btn.addEventListener('click', () => {
        stageEl.src = url;
        railEl.querySelectorAll('.lone-qv__thumb').forEach((b) => b.classList.toggle('is-active', b === btn));
      });
      railEl.appendChild(btn);
    });

    const scrollRail = (dir) => {
      const dy = 88 * dir;
      railEl.scrollBy({ top: dy, behavior: 'smooth' });
    };
    railUp.onclick = () => scrollRail(-1);
    railDown.onclick = () => scrollRail(1);
    const syncRailNav = () => {
      const el = railEl;
      railUp.disabled = el.scrollTop <= 2;
      railDown.disabled = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    };
    railEl.onscroll = syncRailNav;
    setTimeout(syncRailNav, 50);

    zoomBtn.onclick = () => {
      heroInner.classList.toggle('is-zoomed');
      zoomBtn.setAttribute('aria-label', heroInner.classList.contains('is-zoomed') ? 'Zoom out' : 'Zoom image');
      zoomBtn.textContent = heroInner.classList.contains('is-zoomed') ? '−' : '+';
    };

    const colorIdx = findOptionIndex(product, ['color', 'colour']);
    const sizeIdx = findOptionIndex(product, ['size']);

    const getVariantFor = ({ color, size }) => {
      if (colorIdx == null && sizeIdx == null) {
        return variants.find((v) => v && v.available) || variants[0] || null;
      }
      if (colorIdx != null && (color == null || String(color).trim() === '')) return null;
      if (sizeIdx != null && (size == null || String(size).trim() === '')) return null;
      return variants.find((v) => {
        if (!v) return false;
        const opt = v.options || [];
        if (colorIdx != null && String(opt[colorIdx] || '').toLowerCase() !== String(color).toLowerCase()) return false;
        if (sizeIdx != null && String(opt[sizeIdx] || '').toLowerCase() !== String(size).toLowerCase()) return false;
        return true;
      });
    };

    const imageForColor = (colorName) => {
      if (colorIdx == null) return images[0] || featured || '';
      const v =
        variants.find((vv) => {
          const o = vv.options || [];
          return String(o[colorIdx] || '').toLowerCase() === String(colorName).toLowerCase();
        }) || null;
      const src = v ? variantImageSrc(v) : null;
      return src || images[0] || featured || '';
    };

    let chosenColor = null;
    let chosenSizeRaw = null;
    let userPickedColor = colorIdx == null;
    let userPickedSize = sizeIdx == null;

    const sizeSlotsSmxl = buildSmxlSlots(variants, sizeIdx);
    const sizeValuesFallback =
      sizeIdx != null ? Array.from(new Set(variants.map((v) => (v.options || [])[sizeIdx]).filter(Boolean))) : [];

    const selectionComplete = () =>
      userPickedColor &&
      userPickedSize &&
      (colorIdx == null || chosenColor) &&
      (sizeIdx == null || chosenSizeRaw) &&
      getVariantFor({ color: chosenColor, size: chosenSizeRaw });

    const refreshUi = () => {
      const v = selectionComplete() ? getVariantFor({ color: chosenColor, size: chosenSizeRaw }) : null;
      selected = v && v.available ? v : null;

      if (selected) {
        priceNowEl.textContent = money(selected.price);
        if (selected.compare_at_price && selected.compare_at_price > selected.price) {
          priceWasEl.hidden = false;
          priceWasEl.textContent = money(selected.compare_at_price);
        } else {
          priceWasEl.hidden = true;
          priceWasEl.textContent = '';
        }
        updateDiscountPill(selected);
      } else {
        const lo = minAvailablePrice(variants);
        priceWasEl.hidden = true;
        priceWasEl.textContent = '';
        discountEl.hidden = true;
        discountEl.textContent = '';
        priceNowEl.textContent = lo != null ? `From ${money(lo)}` : '—';
      }

      atbBtn.disabled = !(selected && selected.id);

      if (sizeIdx == null) {
        hintEl.textContent = variants.some((x) => x && x.available) ? '' : 'Unavailable';
      } else if (colorIdx != null && !userPickedColor) {
        hintEl.textContent = 'Select a color and a size to add to bag.';
      } else if (!userPickedSize) {
        hintEl.textContent = 'Select a size to add to bag.';
      } else if (!selected) {
        hintEl.textContent = 'This combination is unavailable.';
      } else {
        hintEl.textContent = '';
      }
    };

    const syncStageFromVariant = () => {
      if (colorIdx != null && (!userPickedColor || !chosenColor)) return;
      let v = null;
      if (colorIdx != null && chosenColor) {
        v =
          getVariantFor({ color: chosenColor, size: chosenSizeRaw }) ||
          variants.find((vv) => {
            const o = vv.options || [];
            return String(o[colorIdx] || '').toLowerCase() === String(chosenColor).toLowerCase();
          });
      }
      const src = v ? variantImageSrc(v) : null;
      if (src) {
        stageEl.src = src;
        setActiveThumbByUrl(src);
      }
    };

    const buildSizeButtons = () => {
      sizesEl.innerHTML = '';
      if (sizeIdx == null) return;
      const rows = sizeSlotsSmxl.length
        ? sizeSlotsSmxl
        : sizeValuesFallback.map((raw) => ({ display: String(raw), raw: String(raw) }));
      rows.forEach(({ display, raw }) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        const needsColor = colorIdx != null && !userPickedColor;
        const vFor = getVariantFor({ color: chosenColor, size: raw });
        const unavailable = !needsColor && (!vFor || !vFor.available);
        const isSel = userPickedSize && String(chosenSizeRaw || '').toLowerCase() === String(raw).toLowerCase();
        btn.className = `lone-qv__size${isSel ? ' is-selected' : ''}${unavailable ? ' is-ghost' : ''}`;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', isSel ? 'true' : 'false');
        btn.textContent = display;
        btn.dataset.raw = raw;
        btn.disabled = unavailable;
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          userPickedSize = true;
          chosenSizeRaw = raw;
          sizesEl.querySelectorAll('.lone-qv__size').forEach((b) => {
            const on = b.dataset.raw === raw;
            b.classList.toggle('is-selected', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
          refreshUi();
          syncStageFromVariant();
        });
        sizesEl.appendChild(btn);
      });
    };

    if (colorIdx != null) {
      const colorValues = Array.from(new Set(variants.map((v) => (v.options || [])[colorIdx]).filter(Boolean)));
      colorsBlock.hidden = false;
      colorLabelEl.textContent = '';
      colorValues.forEach((c) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        const imgSrc = imageForColor(c);
        btn.className = 'lone-qv__swatch';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.title = String(c);
        btn.innerHTML = `<img src="${imgSrc}" alt="">`;
        btn.addEventListener('click', () => {
          userPickedColor = true;
          chosenColor = c;
          colorLabelEl.textContent = String(c);
          colorsEl.querySelectorAll('.lone-qv__swatch').forEach((b) => {
            const isSel = b === btn;
            b.classList.toggle('is-selected', isSel);
            b.setAttribute('aria-checked', isSel ? 'true' : 'false');
          });
          buildSizeButtons();
          refreshUi();
          syncStageFromVariant();
        });
        colorsEl.appendChild(btn);
      });
    } else {
      colorsBlock.hidden = true;
    }

    if (sizeIdx != null) {
      buildSizeButtons();
    }

    refreshUi();

    wishlistBtn.onclick = () => {
      const pressed = wishlistBtn.getAttribute('aria-pressed') === 'true';
      wishlistBtn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
    };
    shareBtn.onclick = async () => {
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/products/${handle}`);
        hintEl.textContent = 'Link copied.';
      } catch {
        hintEl.textContent = 'Copy failed.';
      }
    };

    atbBtn.onclick = async () => {
      const line = getVariantFor({ color: chosenColor, size: chosenSizeRaw });
      if (!line || !line.id || !line.available) return;
      atbBtn.disabled = true;
      atbBtn.textContent = 'Adding…';
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.id, quantity: 1 }),
          credentials: 'same-origin'
        });
        if (!r.ok) throw new Error('add failed');
        atbBtn.textContent = 'Added';
        hintEl.textContent = 'Added to bag.';
        setTimeout(() => {
          atbBtn.textContent = 'Add to bag';
          atbBtn.disabled = false;
        }, 900);
      } catch {
        atbBtn.textContent = 'Add to bag';
        atbBtn.disabled = false;
        hintEl.textContent = 'Could not add to bag.';
      }
    };

  };

  const onCardClick = async (e) => {
    const a = e.target.closest('.product-card__figure a, .product-card__title a');
    if (!a) return;

    const card = e.target.closest('product-card');
    if (!card) return;

    if (card.hasAttribute('data-lone-seed')) return;

    const handle = card.getAttribute('handle');
    if (!handle) return;

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    try {
      open();
      await render({ handle });
    } catch (err) {
      close();
      window.location.href = a.href;
    }
  };

  document.addEventListener('click', onCardClick, { passive: false, capture: true });
})();
